import * as React from 'react';
import { Helmet } from 'react-helmet';
import { AppContext, AppContextType } from '../../AppContextType';
import { HeadHelper } from '../../Helpers/HeadHelper';
import { JobVacancy } from '../../Model/JobVacancy';
import { ReferenceIdContainerInterface } from '../../ReferenceIdContainer';
import { getStructuredData } from '../../Service/StructuredData/JobVacancyProvider';
import { UriBuilder } from '../../Service/UriBuilder';
import { StaticSettings } from '../../StaticSettings';
import { Type } from '../../Type';
import { AddToListButton } from '../Button/AddToListButton';
import { ApplyForButton } from '../Button/ApplyForButton';
import { ApplyNowButton } from '../Button/ApplyNowButton';
import { BackButton } from '../Button/BackButton';
import { ShowIcon } from '../Button/ShowIcon';
import { DetailProps } from '../DetailProps';
import { Sharing } from '../Sharing';
import { Trans } from '../Trans';

export class CustomDetail extends React.Component<DetailProps<JobVacancy>> {
    public static contextType = AppContextType;
    public context: AppContext<JobVacancy> = {} as AppContext<JobVacancy>;

    public render() {
        const props = this.props;
        const openModel = props.model;
        const selectedJobVacancies = props.selectedJobVacancies;

        return <div className="job-center-detail-viewport">
            {this.renderHead(openModel)}
            {this.renderInner(openModel, selectedJobVacancies)}
        </div>;
    }

    public componentDidMount() {
        HeadHelper.removeDefaultCanonical();
    }

    private renderInner(job: JobVacancy, selectedJobVacancies: ReferenceIdContainerInterface) {
        const jobStartDate = this.renderJobStartDate(job);
        const workload = this.renderWorkload(job);
        const industry = this.renderIndustry(job);
        const contact = this.renderContact(job);
        const applyMultipleBlock = this.renderApplyMultiple(job, selectedJobVacancies);

        return <section className="job-center-detail-inner">
            <div className="additional-information">
                <div dangerouslySetInnerHTML={{__html: job.additionalInformation}}/>
            </div>

            <h2 className="title">{job.title}</h2>

            {/*<div className="reference-id">*/}
            {/*    <h3>Referenznummer</h3>*/}
            {/*    <p>REF {job.referenceId}</p>*/}
            {/*</div>*/}

            <div className="location">
                <strong><Trans>Arbeitsort:</Trans></strong>&nbsp;
                <span>{job.location}</span>
            </div>

            <div className="employment-type">
                <strong><Trans>Art der Anstellung:</Trans></strong>&nbsp;
                <span>{job.employmentType}</span>
            </div>


            {jobStartDate}
            {workload}

            <div className="description">
                <div dangerouslySetInnerHTML={{__html: job.description}}/>
            </div>


            {industry}

            {contact}

            {/*<div className="hiring-organization">*/}
            {/*    <span>{job.department?.title}</span>*/}
            {/*</div>*/}

            <div className="button-group jc-apply-direct">
                <ApplyForButton model={job}/>

                {this.renderBackButton()}
            </div>

            {applyMultipleBlock}

            <Sharing model={job}/>
        </section>;
    }

    private renderApplyMultiple(job: JobVacancy, selectedJobVacancies: ReferenceIdContainerInterface) {
        if (!StaticSettings.ui.allowApplyToMultiple) {
            return null;
        }
        return <div className="button-group jc-apply-select">
            <AddToListButton model={job}/>
            {selectedJobVacancies.contains(job)
                ? <ApplyNowButton selectedModels={selectedJobVacancies} showIcon={ShowIcon.Left}/>
                : null}
        </div>;
    }

    private renderIndustry(job: JobVacancy) {
        if (StaticSettings.ui.detail.showIndustry && job.industry) {
            return <div className="industry">
                <h3 className="industry-headline"><Trans>Branche</Trans></h3>
                <p>{job.industry?.name}</p>
            </div>;
        } else {
            return null;
        }
    }

    private renderContact(job: JobVacancy) {
        if (!job.applicationContact) {
            return null;
        }

        return <address className="contact">
            <h3 className="contact-headline"><Trans>Kontakt</Trans></h3>
            <div className="contact-name">{job.applicationContact.lastName} {job.applicationContact.firstName}</div>
            <div className="contact-street">{job.applicationContact.street}</div>
            <div className="contact-zip-city">{job.applicationContact.zip} {job.applicationContact.city}</div>

            <div className="contact-email">
                <a href={'mailto:' + job.applicationContact.email}>{job.applicationContact.email}</a>
            </div>
            <div className="contact-telephone">
                <a href={'tel:' + job.applicationContact.telephone}>T {job.applicationContact.telephone}</a>
            </div>
        </address>;
    }

    private renderJobStartDate(job: JobVacancy) {
        if (StaticSettings.ui.showJobStartDate && job.jobStartDate) {
            return <div className="job-start-date">
                <strong><Trans>Stellenantritt:</Trans></strong>
                <span>{job.jobStartDate}</span>
            </div>;
        } else {
            return null;
        }
    }

    private renderWorkload(job: JobVacancy) {
        if (!job.workloadFrom && !job.workloadTo) {
            return null;
        }

        let workloadInner;
        if (job.workloadFrom === job.workloadTo) {
            workloadInner = <>{job.workloadFrom}%</>;
        } else if (job.workloadFrom && job.workloadTo) {
            workloadInner = <>{job.workloadFrom}% - {job.workloadTo}%</>;
        } else if (job.workloadFrom) {
            workloadInner = <>{job.workloadFrom}%</>;
        } else {
            workloadInner = <>{job.workloadTo}%</>;
        }

        return <div className="workload">
            <strong><Trans>Arbeitspensum:</Trans></strong>&nbsp;
            <span>{workloadInner}</span>
        </div>;
    }

    private renderBackButton(className: string = '') {
        return <BackButton history={this.props.history}
                           className={className}
                           text={this.context.i18n.translate('zurück zur Übersicht')}
                           type={Type.JobVacancy}/>;
    }

    private renderHead(job: JobVacancy) {
        const uriBuilder = new UriBuilder();
        const sharingUri = uriBuilder.sharingUriForModel(job);
        const title = this.context.i18n.translate('Stellenangebot %s', [job.title]);

        return <Helmet>
            <title>{StaticSettings.client.name}: {title}</title>
            <link rel="canonical" href={sharingUri}/>
            <script className="structured-data-list" type="application/ld+json">{
                JSON.stringify(getStructuredData(job))
            }</script>
        </Helmet>;
    }
}
